import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
interface Props {
  onNext: () => void;
}
const symbols = ['🎫', '🔬', '✈️', '🤝', '☕', '⚕️'];
interface Card {
  id: number;
  symbol: string;
  flipped: boolean;
  matched: boolean;
}
const buildDeck = (): Card[] => {
  const pairs = [...symbols, ...symbols];
  const shuffled = pairs.map(s => ({
    s,
    r: Math.random()
  })).sort((a, b) => a.r - b.r).map((x, i) => ({
    id: i,
    symbol: x.s,
    flipped: false,
    matched: false
  }));
  return shuffled;
};
export const Screen5Memory = ({
  onNext
}: Props) => {
  const [cards, setCards] = useState<Card[]>(buildDeck);
  const [selected, setSelected] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [completed, setCompleted] = useState(false);
  const handleFlip = (id: number) => {
    if (selected.length >= 2) return;
    if (cards[id].flipped || cards[id].matched) return;
    const newCards = cards.map(c => c.id === id ? {
      ...c,
      flipped: true
    } : c);
    const newSelected = [...selected, id];
    setCards(newCards);
    setSelected(newSelected);
    if (newSelected.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = newSelected;
      if (newCards[a].symbol === newCards[b].symbol) {
        setTimeout(() => {
          setCards(cs => cs.map(c => c.id === a || c.id === b ? {
            ...c,
            matched: true
          } : c));
          setSelected([]);
        }, 500);
      } else {
        setTimeout(() => {
          setCards(cs => cs.map(c => c.id === a || c.id === b ? {
            ...c,
            flipped: false
          } : c));
          setSelected([]);
        }, 900);
      }
    }
  };
  useEffect(() => {
    if (cards.every(c => c.matched)) {
      setTimeout(() => setCompleted(true), 600);
    }
  }, [cards]);
  return <div className="relative w-full h-full bg-slate-950 overflow-hidden flex flex-col" data-id="element-377">
      <div className="absolute inset-0 bg-gradient-to-bl from-red-950/20 via-slate-950 to-slate-950" data-id="element-378" />

      <div className="relative z-10 flex-1 flex flex-col p-8 pt-16" data-id="element-379">
        <motion.span initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-semibold mb-4" data-id="element-380">
          05 — Quick Challenge
        </motion.span>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-2xl font-bold text-white leading-tight" data-id="element-381">
          How switched on <span className="text-red-500" data-id="element-382">are you?</span>
        </motion.h2>
        <p className="text-sm text-slate-400 mb-6 mt-1" data-id="element-383">
          Match all pairs. Moves: {moves}
        </p>

        <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-384">
          {cards.map(card => <motion.button key={card.id} onClick={() => handleFlip(card.id)} whileTap={{
          scale: 0.95
        }} className="aspect-square relative" style={{
          perspective: 1000
        }} data-id="element-385">
              <motion.div animate={{
            rotateY: card.flipped || card.matched ? 180 : 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="absolute inset-0" style={{
            transformStyle: 'preserve-3d'
          }} data-id="element-386">
                {/* Back */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 flex items-center justify-center" style={{
              backfaceVisibility: 'hidden'
            }} data-id="element-387">
                  <div className="w-6 h-6 rounded-full border border-red-500/40 flex items-center justify-center" data-id="element-388">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-389" />
                  </div>
                </div>
                {/* Front */}
                <div className={`absolute inset-0 rounded-2xl flex items-center justify-center text-3xl border ${card.matched ? 'bg-red-600/20 border-red-500/60' : 'bg-slate-900 border-red-500/30'}`} style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }} data-id="element-390">
                  {card.symbol}
                </div>
              </motion.div>
            </motion.button>)}
        </div>

        <AnimatePresence data-id="element-391">
          {completed && <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-gradient-to-br from-red-600/20 to-red-900/20 border border-red-500/40 rounded-2xl p-4 mb-4 flex items-center space-x-3" data-id="element-392">
              <Sparkles className="w-5 h-5 text-red-400" data-id="element-393" />
              <div data-id="element-394">
                <p className="text-sm text-white font-semibold" data-id="element-395">Locked in.</p>
                <p className="text-xs text-slate-400" data-id="element-396">
                  You found every match in {moves} moves.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        <button onClick={onNext} disabled={!completed} className="mt-auto w-full bg-red-600 hover:bg-red-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-semibold py-4 rounded-2xl flex items-center justify-center space-x-2 transition-all active:scale-[0.98]" data-id="element-397">
          <span data-id="element-398">Continue</span>
          <ArrowRight className="w-5 h-5" data-id="element-399" />
        </button>
      </div>
    </div>;
};